/* eslint-disable prettier/prettier */

// Library Imports
import * as React from "react";
import { Grid, Link, Stack } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import AddIcon from "@mui/icons-material/Add";

// app setting imports
import { AuthenticationContext } from "../../../Context";

// importing header helpers
import { getHeaders } from "../../../utils/HeaderHelper";
import { formatToUsDate } from "../../../utils/DateHelper";
import AddLabValues from "./AddLabValues";
import { useParams } from "react-router-dom";
import { useFlash } from "../../../FlashContext";

interface Props {}

const Labs: React.FC<Props> = (props: any) => {
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);
  let { id:patientId } = useParams();

  // For field states
  const [labA1cs, setLabA1cs] = React.useState<any>(null);
  const [labTcs, setLabTcs] = React.useState<any>(null);
  const [labHdls, setLabHdls] = React.useState<any>(null);
  const [openAddModal, setOpenAddModal] = React.useState(false);
  const [labObject, setLabObject] = React.useState<any>();
  const [assets, setAssets] = React.useState<any>({});

  const { showToast } = useFlash();

  React.useEffect(() => {
    getLabsAssets();
    getLabsData();
  }, [patientId]);

  const getLabsAssets = () => {
    fetch("/data_fetching/get_labs_assets", {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.message);
        } else {
          setAssets(result?.resource);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getLabsData = () => {
    if (patientId) {
      fetch(`/data_fetching/get_labs/${patientId}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            console.log(result.message);
          } else {
            setLabA1cs(result?.resource?.hgb_data_values_data);
            setLabHdls(result?.resource?.hdl_values);
            setLabTcs(result?.resource?.tc_values);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handleEdit = (lab: any) => {
    setLabObject(lab);
  };

  return (
    <Grid container className="panel-container">
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid
              container
              direction="row"
              className="admin-header"
            >
              <Grid item xs={12} className="box-header">
                <Stack
                  direction={"row"} 
                  justifyContent={"space-between"} 
                  paddingX={3} 
                  paddingY={1} 
                  alignItems={"center"}
                  display={"flex"}
                >
                  <Grid item>
                    <h3>Labs</h3>
                  </Grid>
                  <Link
                      className="action-link add-encounter"
                      onClick={() => setOpenAddModal(true)}
                    >
                      <AddIcon className="plus-icon" />{" "}
                      <span className="app-user-text">Add New Lab Value</span>
                    </Link>
                </Stack>
              </Grid>
            </Grid>
            <Grid
                container
                className="labs-container grey-container pad-top-10"
              >
                <Grid item xs={12} className="data-body-container">
                  <Grid container className="data-container-parent">
                    <Grid item xs={4} className="data-container" style={{ paddingRight: "2%" }}>
                      <div className="single-lab-header">A1c</div>
                      {labA1cs != null && labA1cs.length ? (
                        <>
                          <div className="scrollable-container">
                            {labA1cs.map((lab, index) => {
                              return (
                                <div 
                                  key={index} 
                                  className="data-row" 
                                  title="Edit"  
                                  style={{
                                    cursor: "auto"
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      width: "60%"
                                    }}
                                  >
                                    <span className="data-value">{lab.reading_value}</span>{" "}
                                    {
                                      lab?.encounter_status === "signed" &&
                                      <Link
                                        href={`/patient_reports/${lab.patient_id}/patient_encounters/new?encounter_id=${lab.encounter_uuid}&view_only=1`}
                                        style={{
                                          color: "#3F51B5",
                                          fontSize: "14px",
                                          fontWeight: 700,
                                          lineHeight: "17px",
                                          textDecoration: "underline"
                                        }}
                                      >
                                        View Related Encounter
                                      </Link>
                                    }
                                  </div> 
                                  <div
                                     style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      width: "32%"
                                    }}
                                  >
                                    <span className="data-date">
                                      {formatToUsDate(lab.date_recorded)}
                                    </span>
                                    {
                                      lab?.encounter_status !== "signed" &&
                                        <Link
                                          onClick={() => lab.id ? handleEdit(lab) : alert("This data is from NDIIHN and can not be edited.")}
                                        >
                                          <img
                                            alt="Edit"
                                            className="action-icon-image"
                                            src={assets.pencil_grey}
                                            style={{ width: "18px", height: "18px" }}
                                          />
                                        </Link>
                                    }
                                  </div>                              
                                </div>
                              );
                            })} 
                          </div>
                          <div className="data-row-caret">
                          </div>
                        </>
                      ) : (
                        <>
                          {labA1cs == null ? (
                            <div className="data-row">
                              <span className="data-value">Loading</span>
                            </div>
                          ) : (
                            <div className="data-row">
                              <span className="data-value">N/A</span>
                            </div>
                          )}
                        </>
                      )}
                    </Grid>
                    <Grid item xs={4} className="data-container" style={{ paddingRight: "2%" }}>
                      <div className="single-lab-header">TC</div>
                      {labTcs != null && labTcs.length ? (
                        <>
                          <div className="scrollable-container">
                            {labTcs.map((lab, index) => {
                              return (
                                <div 
                                  key={index} 
                                  className="data-row" 
                                  title="Edit"
                                  style={{
                                    cursor: "auto"
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "60%"
                                    }}
                                  >
                                    <span className="data-value">{lab.reading_value}</span>{" "}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      width: "29%"
                                    }}
                                  >
                                    <span className="data-date">
                                      {formatToUsDate(lab.date_recorded)}
                                    </span>
                                    <Link
                                      onClick={() => lab.id ? handleEdit(lab) : alert("This data is from NDIIHN and can not be edited.")}
                                    >
                                      <img
                                        alt="Edit"
                                        className="action-icon-image"
                                        src={assets.pencil_grey}
                                        style={{ width: "18px", height: "18px" }}
                                      />
                                    </Link>
                                  </div>
                                </div>
                              );
                            })}
                            </div>
                          <div className="data-row-caret">
                          </div>                          
                        </>
                      ) : (
                        <>
                          {labTcs == null ? (
                            <div className="data-row">
                              <span className="data-value">Loading</span>
                            </div>
                          ) : (
                            <div className="data-row">
                              <span className="data-value">N/A</span>
                            </div>
                          )}
                        </>
                      )}
                    </Grid>
                    <Grid item xs={4} className="data-container">
                      <div className="single-lab-header">HDL</div>
                      {labHdls != null && labHdls.length ? (
                        <>
                          <div className="scrollable-container">
                            {labHdls.map((lab, index) => {
                              return (
                                <div 
                                  key={index} 
                                  className="data-row" 
                                  title="Edit"
                                  style={{
                                    cursor: "auto"
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "60%"
                                    }}
                                  >
                                    <span className="data-value">{lab.reading_value}</span>{" "}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      width: "27%"
                                    }}
                                  >
                                    <span className="data-date">
                                      {formatToUsDate(lab.date_recorded)}
                                    </span>
                                    <Link
                                      onClick={() => lab.id ? handleEdit(lab) : alert("This data is from NDIIHN and can not be edited.")}
                                    >
                                      <img
                                        alt="Edit"
                                        className="action-icon-image"
                                        src={assets.pencil_grey}
                                        style={{ width: "18px", height: "18px" }}
                                      />
                                    </Link>
                                  </div>
                                </div>
                              );
                            })}
                            </div>

                          <div className="data-row-caret">
                          </div>
                        </>
                      ) : (
                        <>
                          {labHdls == null ? (
                            <div className="data-row">
                              <span className="data-value">Loading</span>
                            </div>
                          ) : (
                            <div className="data-row">
                              <span className="data-value">N/A</span>
                            </div>
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
          </Grid>
        </Grid>
      </Grid>
      {open && (
        <AddLabValues
          patient_id={patientId}
          open={openAddModal}
          setOpenAddModal={setOpenAddModal}
          onSuccess={(a1cValue) => {
            history.pushState(null, null, '#labs');
            window.location.reload();
          }}
          onModalClose={() => setLabObject(undefined)}
          labObject={labObject}
        />
      )}
    </Grid>
  );
};

export default Labs;